import * as THREE from "three";
import { scene } from "./core";
import {
  getPlayerMeshes,
  getRespawnEffects,
  getLocalPlayer,
  keys,
  getLocalPlayerId,
} from "./gameState";
import { sendMessage } from "./socket";
import { GAME_CONFIG, VISUAL_CONFIG } from "@shared/config";
import { setMeshOpacity, updateMeshOpacity } from "./utils";

// Apply respawn visual effect to player
export function applyRespawnEffect(
  playerId: string,
  mesh: THREE.Object3D,
  duration = GAME_CONFIG.PLAYER.RESPAWN_TIME
): void {
  // Create the effect entry
  const respawnEffects = getRespawnEffects();
  respawnEffects[playerId] = {
    startTime: Date.now(),
    duration,
    mesh,
  };

  // Make player semi-transparent and pulsing using the helper
  setMeshOpacity(mesh, 0.5, true);

  // Add a visual effect around the respawning player
  const effectGeometry = new THREE.RingGeometry(0.5, 0.6, 32);
  const effectMaterial = new THREE.MeshBasicMaterial({
    color: 0x00ffff,
    transparent: true,
    opacity: 0.7,
    side: THREE.DoubleSide,
  });

  const effectMesh = new THREE.Mesh(effectGeometry, effectMaterial);
  effectMesh.rotation.x = -Math.PI / 2; // Make it horizontal
  effectMesh.position.copy(mesh.position);
  effectMesh.position.y = 0.05; // Just above the ground

  scene.add(effectMesh);

  // Store the effect mesh so we can update and remove it
  respawnEffects[playerId].mesh = effectMesh;
}

// Use rocket function remains unchanged
export function useRocket(): void {
  const localPlayer = getLocalPlayer();
  if (!localPlayer || !localPlayer.hasRocket) {
    console.log("No rocket available!");
    return;
  }

  let direction = -1;
  if (keys.w || keys.ArrowUp) {
    direction = 0; // North
  } else if (keys.d || keys.ArrowRight) {
    direction = 1; // East
  } else if (keys.s || keys.ArrowDown) {
    direction = 2; // South
  } else if (keys.a || keys.ArrowLeft) {
    direction = 3; // West
  }

  if (direction === -1) {
    direction = 0;
  }

  sendMessage("rocket", { direction });
}

// Update respawn effects (called in animation loop)
export function updateRespawnEffects(): void {
  const currentTime = Date.now();
  const respawnEffects = getRespawnEffects();
  const playerMeshes = getPlayerMeshes();

  for (const playerId in respawnEffects) {
    const effect = respawnEffects[playerId];
    const elapsedTime = currentTime - effect.startTime;
    const playerMesh = playerMeshes[playerId];
    if (elapsedTime >= effect.duration) {
      if (playerMesh) {
        setMeshOpacity(playerMesh, 1.0, false);
      }
      if (effect.mesh) {
        scene.remove(effect.mesh);
      }
      delete respawnEffects[playerId];
      continue;
    }

    if (effect.mesh) {
      const pulseFactor =
        0.7 +
        0.3 *
          Math.sin(elapsedTime / VISUAL_CONFIG.ANIMATION.RESPAWN_PULSE_SPEED);
      effect.mesh.scale.set(pulseFactor, pulseFactor, pulseFactor);
      effect.mesh.rotation.z += 0.02;

      if (playerMesh) {
        const opacity =
          0.3 +
          0.5 *
            Math.sin(elapsedTime / VISUAL_CONFIG.ANIMATION.RESPAWN_PULSE_SPEED);
        updateMeshOpacity(playerMesh, opacity);
      }

      if (playerMesh) {
        effect.mesh.position.set(
          playerMesh.position.x,
          0.05,
          playerMesh.position.z
        );
      }
    }
  }
}

/**
 * Create collision effect between player and ghost.
 */
export function createCollisionEffect(
  playerPosition: THREE.Vector3,
  ghostPosition: THREE.Vector3
): void {
  const particleCount = 20;
  const explosionGroup = new THREE.Group();
  const midPoint = new THREE.Vector3()
    .addVectors(playerPosition, ghostPosition)
    .multiplyScalar(0.5);

  for (let i = 0; i < particleCount; i++) {
    const particleGeometry = new THREE.SphereGeometry(0.1, 4, 4);
    const particleMaterial = new THREE.MeshBasicMaterial({
      color: Math.random() > 0.5 ? 0xff0000 : 0xffff00,
      transparent: true,
    });

    const particle = new THREE.Mesh(particleGeometry, particleMaterial);
    particle.position.set(
      midPoint.x + (Math.random() - 0.5) * 0.5,
      midPoint.y + Math.random() * 0.5,
      midPoint.z + (Math.random() - 0.5) * 0.5
    );

    particle.userData = {
      originalPos: particle.position.clone(),
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 0.05,
        Math.random() * 0.05,
        (Math.random() - 0.5) * 0.05
      ),
      age: 0,
      maxAge: 30 + Math.random() * 30,
    };

    explosionGroup.add(particle);
  }

  scene.add(explosionGroup);

  const animateExplosion = () => {
    let alive = false;
    for (const particle of explosionGroup.children) {
      const userData = particle.userData;
      userData.age++;
      if (userData.age < userData.maxAge) {
        alive = true;
        particle.position.add(userData.velocity);
        userData.velocity.y -= 0.001;
        const opacity = 1 - userData.age / userData.maxAge;
        ((particle as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity =
          opacity;
        const scale = 1 - (userData.age / userData.maxAge) * 0.5;
        particle.scale.set(scale, scale, scale);
      } else {
        particle.visible = false;
      }
    }
    if (alive) {
      requestAnimationFrame(animateExplosion);
    } else {
      scene.remove(explosionGroup);
    }
  };
  animateExplosion();
}

// Enhanced particle effects for movement
export function updateMovementEffects(): void {
  const localId = getLocalPlayerId();
  if (!localId) return;
  const localMesh = getPlayerMeshes()[localId];
  if (localMesh && Math.random() < 0.15) {
    // Increased particle emission rate
    createEnhancedDustEffect(localMesh.position);
  }
}

// More visually impressive dust effect
function createEnhancedDustEffect(position: THREE.Vector3): void {
  const particleCount = 8; // More particles
  const colors = [0x55aaff, 0x88ccff, 0xaaddff]; // Blue palette to match theme

  const particleGroup = new THREE.Group();
  scene.add(particleGroup);

  for (let i = 0; i < particleCount; i++) {
    const particleSize = 0.05 + Math.random() * 0.15; // Varied sizes
    const geometry = new THREE.SphereGeometry(particleSize, 6, 6);
    const material = new THREE.MeshStandardMaterial({
      color: colors[Math.floor(Math.random() * colors.length)],
      emissive: 0x3366ff,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.7,
    });

    const particle = new THREE.Mesh(geometry, material);

    // Position around the player
    particle.position.set(
      position.x + (Math.random() - 0.5) * 0.4,
      position.y + Math.random() * 0.2,
      position.z + (Math.random() - 0.5) * 0.4
    );

    // Add velocity to each particle
    particle.userData.velocity = new THREE.Vector3(
      (Math.random() - 0.5) * 0.05,
      Math.random() * 0.07,
      (Math.random() - 0.5) * 0.05
    );

    // Add lifetime data
    particle.userData.life = 0;
    particle.userData.maxLife = 30 + Math.random() * 20;

    particleGroup.add(particle);
  }

  // Animate the particles
  function animateParticles() {
    let allDead = true;

    for (const child of particleGroup.children) {
      const particle = child as THREE.Mesh;
      particle.userData.life++;

      if (particle.userData.life < particle.userData.maxLife) {
        allDead = false;

        // Move the particle
        particle.position.add(particle.userData.velocity);

        // Slow down over time
        particle.userData.velocity.multiplyScalar(0.95);

        // Fade out
        const opacity = 1 - particle.userData.life / particle.userData.maxLife;
        (particle.material as THREE.MeshStandardMaterial).opacity = opacity;

        // Shrink slightly
        const scale =
          1 - (particle.userData.life / particle.userData.maxLife) * 0.5;
        particle.scale.set(scale, scale, scale);
      } else {
        particle.visible = false;
      }
    }

    if (allDead) {
      scene.remove(particleGroup);
      return;
    }

    requestAnimationFrame(animateParticles);
  }

  animateParticles();
}

// Enhanced collection effect with glow
export function handleCollectEffect(position: THREE.Vector3): void {
  // Create expanding ring effect
  const ringGeometry = new THREE.RingGeometry(0.1, 0.2, 16);
  const ringMaterial = new THREE.MeshBasicMaterial({
    color: 0xffff00,
    transparent: true,
    opacity: 0.8,
    side: THREE.DoubleSide,
  });

  const ring = new THREE.Mesh(ringGeometry, ringMaterial);
  ring.position.copy(position);
  ring.rotation.x = Math.PI / 2; // Lay flat
  scene.add(ring);

  // Add sparkles
  createSparkleEffect(position);

  // Animate the ring
  let scale = 1;
  function animateRing() {
    scale += 0.15;
    ring.scale.set(scale, scale, scale);

    (ring.material as THREE.MeshBasicMaterial).opacity -= 0.035;

    if ((ring.material as THREE.MeshBasicMaterial).opacity <= 0) {
      scene.remove(ring);
      return;
    }

    requestAnimationFrame(animateRing);
  }

  animateRing();
}

// Helper functions for particle effects
function createSparkleEffect(position: THREE.Vector3): void {
  const particleCount = 10;
  const particles = new THREE.Points(
    new THREE.BufferGeometry(),
    new THREE.PointsMaterial({
      size: 0.15,
      transparent: true,
      opacity: 0.7,
      color: 0xffff00, // Yellow sparkles
    })
  );

  const positions = new Float32Array(particleCount * 3);
  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = position.x + (Math.random() - 0.5) * 0.3;
    positions[i * 3 + 1] = position.y + Math.random() * 0.3;
    positions[i * 3 + 2] = position.z + (Math.random() - 0.5) * 0.3;
  }
  particles.geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(positions, 3)
  );
  scene.add(particles);

  setTimeout(() => scene.remove(particles), 300);
}
